import React, { useState, useCallback, useEffect } from 'react'
import type { Editor } from '@tiptap/react'
import { SelectionFeedbackInput } from './SelectionFeedbackInput'
import { hasProcessingHighlight } from './ProcessingHighlightExtension'

interface EditorToolbarProps {
  editor: Editor | null
  onFeedbackSubmit?: (feedback: string) => void
}

interface ToolbarButtonProps {
  onClick: () => void
  isActive?: boolean
  disabled?: boolean
  children: React.ReactNode
  title?: string
}

function ToolbarButton({ onClick, isActive, disabled, children, title }: ToolbarButtonProps) {
  return (
    <button
      type='button'
      onClick={onClick}
      onMouseDown={e => e.preventDefault()}
      disabled={disabled}
      title={title}
      className={`
        flex items-center justify-center w-9 h-9 rounded transition-colors
        ${
          isActive
            ? 'bg-tokens-primary-3 text-tokens-primary-2'
            : 'text-tokens-neutral-text-primary hover:bg-tokens-neutral-fill-fill-6'
        }
        ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      {children}
    </button>
  )
}

function ToolbarDivider() {
  return (
    <div className='w-[5px] h-4 flex items-center justify-center'>
      <div className='w-px h-full bg-tokens-neutral-fill-fill-6' />
    </div>
  )
}

/**
 * Floating toolbar for the canvas editor.
 *
 * States:
 * - Default: formatting buttons + "Improve" button
 * - Feedback: text input for feedback
 *
 * Note: Processing state is handled by CanvasEditor (overlay) and
 * EditableCanvasSplitScreen (status indicator), not here.
 */
export function EditorToolbar({ editor, onFeedbackSubmit }: EditorToolbarProps) {
  const [isFeedbackMode, setIsFeedbackMode] = useState(false)

  // Clean up decoration if selection is lost while in feedback mode (user clicked away)
  useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      const { from, to } = editor.state.selection
      // If selection collapsed and there's a decoration, clean it up
      if (from === to && hasProcessingHighlight(editor)) {
        editor.commands.unsetProcessingHighlight()
        setIsFeedbackMode(false)
      }
    }

    editor.on('selectionUpdate', handleSelectionUpdate)
    return () => editor.off('selectionUpdate', handleSelectionUpdate)
  }, [editor])

  const handleImproveClick = useCallback(() => {
    if (onFeedbackSubmit && editor) {
      const { from, to } = editor.state.selection
      editor.commands.setProcessingHighlight({ from, to })
      setIsFeedbackMode(true)
    }
  }, [onFeedbackSubmit, editor])

  const handleFeedbackSubmit = useCallback(
    (feedback: string) => {
      if (onFeedbackSubmit) {
        onFeedbackSubmit(feedback)
      }
      // Toolbar will unmount when parent sets isProcessing=true (BubbleMenu hidden)
      // No need to reset isFeedbackMode - component will unmount
    },
    [onFeedbackSubmit]
  )

  const handleFeedbackCancel = useCallback(() => {
    if (editor) {
      editor.commands.unsetProcessingHighlight()
    }
    setIsFeedbackMode(false)
  }, [editor])

  if (!editor) return null

  // Show feedback input mode
  if (isFeedbackMode) {
    return (
      <SelectionFeedbackInput
        onSubmit={handleFeedbackSubmit}
        onCancel={handleFeedbackCancel}
        placeholder='What would you like to change?'
      />
    )
  }

  return (
    <div className='flex items-center py-2 pr-1 bg-white rounded-2xl border border-tokens-neutral-border-2 shadow-popover'>
      {/* Improve Button (AI) - opens feedback input */}
      <button
        type='button'
        onClick={handleImproveClick}
        onMouseDown={e => e.preventDefault()} // Prevent stealing focus from editor
        className={`flex items-center gap-2 h-9 px-4 py-2 rounded-xl transition-colors ${
          onFeedbackSubmit
            ? 'hover:bg-tokens-neutral-fill-fill-6 cursor-pointer'
            : 'opacity-50 cursor-not-allowed'
        }`}
        title={onFeedbackSubmit ? 'Improve with AI' : 'Improve with AI (not available)'}
        disabled={!onFeedbackSubmit}
      >
        <SparkleIcon />
        <span className='font-geist font-medium text-sm leading-[22px] text-tokens-neutral-text-primary'>
          Improve
        </span>
      </button>

      <ToolbarDivider />

      {/* Text Style Dropdown */}
      <div className='relative flex items-center gap-2 h-9 px-4 py-2 cursor-pointer hover:bg-tokens-neutral-fill-fill-6 rounded transition-colors'>
        <select
          className='appearance-none bg-transparent font-geist font-medium text-sm leading-[22px] text-tokens-neutral-text-primary cursor-pointer focus:outline-none pr-4'
          value={
            editor.isActive('heading', { level: 1 })
              ? 'h1'
              : editor.isActive('heading', { level: 2 })
                ? 'h2'
                : editor.isActive('heading', { level: 3 })
                  ? 'h3'
                  : 'paragraph'
          }
          onChange={e => {
            const value = e.target.value
            if (value === 'paragraph') {
              editor.chain().focus().setParagraph().run()
            } else {
              const level = parseInt(value.replace('h', '')) as 1 | 2 | 3
              editor.chain().focus().toggleHeading({ level }).run()
            }
          }}
        >
          <option value='paragraph'>Body</option>
          <option value='h1'>Heading 1</option>
          <option value='h2'>Heading 2</option>
          <option value='h3'>Heading 3</option>
        </select>
        <div className='absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none'>
          <ChevronDownIcon />
        </div>
      </div>

      <ToolbarDivider />

      {/* Formatting buttons */}
      <div className='flex items-center gap-1 pr-2'>
        {/* Bold */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          title='Bold (Cmd+B)'
        >
          <BoldIcon />
        </ToolbarButton>

        {/* Italic */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          title='Italic (Cmd+I)'
        >
          <ItalicIcon />
        </ToolbarButton>

        {/* Underline */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          disabled={!editor.can().chain().focus().toggleUnderline().run()}
          title='Underline (Cmd+U)'
        >
          <UnderlineIcon />
        </ToolbarButton>

        {/* Strikethrough */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
          title='Strikethrough'
        >
          <StrikethroughIcon />
        </ToolbarButton>

        {/* Code */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          isActive={editor.isActive('code')}
          disabled={!editor.can().chain().focus().toggleCode().run()}
          title='Code'
        >
          <CodeIcon />
        </ToolbarButton>
      </div>
    </div>
  )
}

function ChevronDownIcon() {
  return (
    <svg
      width='8'
      height='8'
      viewBox='0 0 8 8'
      fill='none'
      className='text-tokens-neutral-text-primary'
    >
      <path
        d='M1.5 3L4 5.5L6.5 3'
        stroke='currentColor'
        strokeWidth='1.2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

function BoldIcon() {
  return (
    <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
      <path
        d='M5 3.333h6.25a3.125 3.125 0 0 1 0 6.25H5V3.333ZM5 9.583h7.083a3.542 3.542 0 0 1 0 7.084H5V9.583Z'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

function ItalicIcon() {
  return (
    <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
      <path
        d='M15 3.333H7.5M12.5 16.667H5M11.667 3.333 8.333 16.667'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

function UnderlineIcon() {
  return (
    <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
      <path
        d='M5 3.333v5.834a5 5 0 0 0 10 0V3.333M3.333 16.667h13.334'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

function StrikethroughIcon() {
  return (
    <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
      <path
        d='M3.333 10h13.334M13.75 5.833A3.333 3.333 0 0 0 10.417 2.5H9.167a3.333 3.333 0 0 0 0 6.667h1.666M6.25 14.167a3.333 3.333 0 0 0 3.333 3.333h1.25a3.333 3.333 0 0 0 0-6.667H9.167'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

function CodeIcon() {
  return (
    <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
      <path
        d='M13.333 5 17.5 10l-4.167 5M6.667 15 2.5 10l4.167-5'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

function SparkleIcon() {
  return (
    <svg width='13' height='11' viewBox='0 0 13 11' fill='none' className='text-tokens-primary-2'>
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M0 1.72289C0 1.35692 0.282539 1.06024 0.631068 1.06024H3.15534C3.50387 1.06024 3.78641 1.35692 3.78641 1.72289C3.78641 2.08886 3.50387 2.38554 3.15534 2.38554H2.52427V9.6747H3.15534C3.50387 9.6747 3.78641 9.97138 3.78641 10.3373C3.78641 10.7033 3.50387 11 3.15534 11H0.631068C0.282539 11 0 10.7033 0 10.3373C0 9.97138 0.282539 9.6747 0.631068 9.6747H1.26214V2.38554H0.631068C0.282539 2.38554 0 2.08886 0 1.72289Z'
        fill='currentColor'
      />
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M7.25728 3.71084C7.52891 3.71084 7.77007 3.89336 7.85597 4.16395L8.46614 6.08607L10.2966 6.72678C10.5543 6.81697 10.7282 7.0702 10.7282 7.35542C10.7282 7.64065 10.5543 7.89387 10.2966 7.98407L8.46614 8.62477L7.85597 10.5469C7.77007 10.8175 7.52891 11 7.25728 11C6.98565 11 6.7445 10.8175 6.6586 10.5469L6.04843 8.62477L4.21791 7.98407C3.96022 7.89387 3.78641 7.64065 3.78641 7.35542C3.78641 7.0702 3.96022 6.81697 4.21791 6.72678L6.04843 6.08607L6.6586 4.16395C6.7445 3.89336 6.98565 3.71084 7.25728 3.71084ZM7.25728 6.46898L7.14601 6.81949C7.0832 7.01736 6.93533 7.17263 6.74689 7.23858L6.41309 7.35542L6.74689 7.47226C6.93533 7.53821 7.0832 7.69348 7.14601 7.89135L7.25728 8.24186L7.36855 7.89135C7.43136 7.69348 7.57923 7.53821 7.76767 7.47226L8.10148 7.35542L7.76767 7.23858C7.57923 7.17263 7.43136 7.01736 7.36855 6.81949L7.25728 6.46898Z'
        fill='currentColor'
      />
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M10.7282 0C10.8911 0 11.0358 0.109508 11.0874 0.271861L11.5008 1.57423L12.7411 2.00835C12.8957 2.06247 13 2.21441 13 2.38554C13 2.55668 12.8957 2.70861 12.7411 2.76273L11.5008 3.19685L11.0874 4.49922C11.0358 4.66158 10.8911 4.77108 10.7282 4.77108C10.5652 4.77108 10.4205 4.66158 10.3689 4.49922L9.95551 3.19685L8.71521 2.76273C8.5606 2.70861 8.45631 2.55668 8.45631 2.38554C8.45631 2.21441 8.5606 2.06247 8.71521 2.00835L9.95551 1.57423L10.3689 0.271861C10.4205 0.109508 10.5652 0 10.7282 0Z'
        fill='currentColor'
      />
    </svg>
  )
}
